import { t as tr } from '../i18n.js'
import { useState, type FormEvent } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { Navigate, useSearchParams } from 'react-router'
import { api, ApiError, browserTimezone, type User } from '../api.js'
import { useAuth } from '../auth.js'

const ERRORS: Record<string, string> = {
  oidc_state: 'The Google sign-in expired or was tampered with. Please try again.',
  oidc_failed: 'Google sign-in failed. Please try again.',
  oidc_unverified: 'Your Google account email is not verified.',
  not_invited: 'That Google account has not been invited. Ask an administrator for an invite.',
  deactivated: 'This account has been deactivated.',
  ip_blocked: 'This account may not be used from your current network address.',
}

export function LoginPage() {
  const { user, googleEnabled, refresh } = useAuth()
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(ERRORS[params.get('error') ?? ''] ?? null)
  const [busy, setBusy] = useState(false)
  const [mfaStep, setMfaStep] = useState(false)
  const [code, setCode] = useState('')

  if (user) return <Navigate to={next} replace />

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const r = await api<{ user: User | null; mfaRequired: boolean }>('/auth/login', { method: 'POST', json: { email, password } })
      if (r.mfaRequired) { setMfaStep(true); setPassword('') } else await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed')
    } finally {
      setBusy(false)
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      await api('/auth/mfa/verify', { method: 'POST', json: { code } })
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? (err.status === 401 && /sign in again/.test(err.message) ? 'That took too long — start again.' : err.message) : 'Verification failed')
      if (err instanceof ApiError && /sign in again/.test(err.message)) { setMfaStep(false); setCode('') }
    } finally {
      setBusy(false)
    }
  }

  async function passkey() {
    setBusy(true); setError(null)
    try {
      const options = await api<Parameters<typeof startAuthentication>[0]['optionsJSON']>('/auth/passkeys/login/options', { method: 'POST' })
      const response = await startAuthentication({ optionsJSON: options })
      await api('/auth/passkeys/login/verify', { method: 'POST', json: { response } })
      await refresh()
    } catch (err) {
      if (err instanceof ApiError) setError(err.message)
      else if (err instanceof Error && err.name === 'NotAllowedError') setError(null)   // user cancelled the browser prompt
      else setError('Passkey sign-in failed')
    } finally {
      setBusy(false)
    }
  }

  const googleHref = `/api/auth/google?tz=${encodeURIComponent(browserTimezone())}&next=${encodeURIComponent(next)}`

  return (
    <main className="shell narrow">
      <header>
        <h1>ADKOM Product Manager</h1>
        <p className="muted">{tr("Sign in")}</p>
      </header>
      <section className="card stack">
        {mfaStep ? (
          <form onSubmit={submitCode} className="stack">
            <p>{tr("Enter the 6-digit code from your authenticator app, or one of your recovery codes.")}</p>
            <label>{tr("Code")}
              <input autoFocus inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
            </label>
            {error && <p className="bad" role="alert">{error}</p>}
            <button className="button primary" type="submit" disabled={busy}>{busy ? tr("Checking…") : tr("Verify")}</button>
            <button className="link" type="button" onClick={() => { setMfaStep(false); setCode(''); setError(null) }}>Start over</button>
          </form>
        ) : (<>
        {googleEnabled && (
          <>
            <a className="button primary block" href={googleHref}>{tr("Continue with Google")}</a>
            <div className="divider">{tr("or use a password")}</div>
          </>
        )}
        <form onSubmit={submit} className="stack">
          <label>{tr("Email")}
            <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>{tr("Password")}
            <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          {error && <p className="bad" role="alert">{error}</p>}
          <button className="button" type="submit" disabled={busy}>{busy ? tr("Signing in…") : tr("Sign in")}</button>
        </form>
        {typeof window !== 'undefined' && 'PublicKeyCredential' in window && (
          <button className="button block" type="button" onClick={() => void passkey()} disabled={busy}>Sign in with a passkey</button>
        )}
        <p className="muted small">{tr("Accounts are invite-only. If you have an invite link, open it to set up your account.")}</p>
        </>)}
      </section>
    </main>
  )
}

function safeNext(v: string | null): string {
  return v && v.startsWith('/') && !v.startsWith('//') ? v : '/'
}
